/**
 * Image compression utility for candidate photographs
 * Compresses images to target size (max 1-2 MB)
 */

/**
 * Compress image to target size (max 1-2 MB)
 * @param file - Image file to compress
 * @param maxSizeMB - Maximum size in MB (default: 1.5)
 * @param maxWidth - Maximum width in pixels (default: 800)
 * @param maxHeight - Maximum height in pixels (default: 800)
 * @returns Compressed image as Blob
 */
export async function compressImage(
  file: File,
  maxSizeMB: number = 1.5,
  maxWidth: number = 800,
  maxHeight: number = 800
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const img = new Image()
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width
        let height = img.height
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }
        
        ctx.drawImage(img, 0, 0, width, height)
        
        // Try different quality levels to meet size requirement
        const maxSizeBytes = maxSizeMB * 1024 * 1024
        let quality = 0.9
        let compressedBlob: Blob | null = null
        
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'))
                return
              }
              
              // If size is acceptable or quality is too low, return
              if (blob.size <= maxSizeBytes || quality <= 0.1) {
                resolve(blob)
                return
              }
              
              // Reduce quality and try again
              quality -= 0.1
              tryCompress()
            },
            'image/jpeg',
            quality
          )
        }
        
        tryCompress()
      }
      
      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }
      
      img.src = e.target?.result as string
    }
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }
    
    reader.readAsDataURL(file)
  })
}

/**
 * Capture photo from video stream
 * @param videoElement - HTML video element with active stream
 * @returns Blob of captured image
 */
export function capturePhotoFromVideo(videoElement: HTMLVideoElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = videoElement.videoWidth
    canvas.height = videoElement.videoHeight
    
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new Error('Could not get canvas context'))
      return
    }
    
    // Draw video frame to canvas
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height)
    
    // Convert to blob with compression
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to capture photo'))
          return
        }
        resolve(blob)
      },
      'image/jpeg',
      0.85 // Initial quality
    )
  })
}

